import type { GameName, View } from '../types';

// La vista actual, escrita en el hash de la URL: `#/detail/ANIMAL`,
// `#/cuento-reader/3`, `#/game/memo`, `#/admin`. El home es la URL sin hash.
// Es lo que permite recargar sin volver al home, usar el atrás del navegador
// y abrir una vista en una pestaña nueva (las vistas previas del admin).
//
// Toda vista tiene a lo sumo un parámetro, así que alcanza con
// `#/<nombre>/<parámetro>`. Un `Record` sobre `View['name']` obliga a decidir
// qué lleva cada vista nueva: si se agrega una al union y no acá, no compila.

type Param = 'category' | 'idx' | 'game' | 'filter' | null;

const PARAMS: Record<View['name'], Param> = {
  home: null,
  detail: 'category',
  letras: null,
  silabas: null,
  'buscar-list': null,
  'buscar-scene': 'idx',
  'cuentos-list': null,
  'cuento-reader': 'idx',
  'jw-historias-list': null,
  'jw-historia-reader': 'idx',
  'jw-comics-list': null,
  'jw-comic-reader': 'idx',
  'jw-videos-list': null,
  'jw-video-player': 'idx',
  game: 'game',
  admin: null,
};

// Mismo truco: un juego nuevo en `GameName` que falte acá no compila, y un
// `#/game/lo-que-sea` inventado no llega a montar una vista vacía.
const GAMES: Record<GameName, true> = {
  memo: true,
  'memo-easy': true,
  drag: true,
  canvas: true,
  voice: true,
  phone: true,
  typing: true,
  musica: true,
  calculator: true,
  contar: true,
  oraciones: true,
  tren: true,
  'sonido-inicial': true,
  cazador: true,
  'maquina-oraciones': true,
  mente: true,
  rompecabezas: true,
  barrera: true,
  vestir: true,
  pintar: true,
};

export function viewToHash(view: View): string {
  if (view.name === 'home') return '';
  const key = PARAMS[view.name];
  const value = key ? (view as Record<string, unknown>)[key] : undefined;
  return value === undefined
    ? `#/${view.name}`
    : `#/${view.name}/${encodeURIComponent(String(value))}`;
}

/** La vista que describe un hash, o null si no describe ninguna válida. */
export function hashToView(hash: string): View | null {
  // Acepta `#/admin` y también la forma vieja sin barra (`#admin`).
  const raw = hash.replace(/^#\/?/, '');
  if (!raw) return null;
  const slash = raw.indexOf('/');
  const name = slash === -1 ? raw : raw.slice(0, slash);
  const rest = slash === -1 ? undefined : raw.slice(slash + 1);
  if (!Object.hasOwn(PARAMS, name)) return null;
  const key = PARAMS[name as View['name']];
  if (key === null) return { name } as View;
  if (rest === undefined || rest === '') return null;

  let value: string;
  try {
    value = decodeURIComponent(rest);
  } catch {
    return null;
  }
  if (key === 'idx') {
    const idx = Number(value);
    if (!Number.isInteger(idx) || idx < 0) return null;
    return { name, idx } as View;
  }
  if (key === 'game' && !Object.hasOwn(GAMES, value)) return null;
  return { name, [key]: value } as View;
}
